import { useRef, type ReactNode } from 'react';

import { Button } from './Button';
import Modal from './Modal';

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  /** What happens if the person confirms, in plain words. */
  consequence: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Destructive actions get the danger style; focus starts on Cancel. */
  destructive?: boolean;
  isPending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Asks before an action that stops, deletes or cannot be undone. */
export default function ConfirmDialog({
  isOpen,
  title,
  consequence,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  isPending = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      size="sm"
      initialFocusRef={cancelRef}
      footer={
        <div className="flex justify-end gap-2">
          <Button ref={cancelRef} variant="secondary" onClick={onCancel} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            isLoading={isPending}
          >
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="text-body text-ink">{consequence}</div>
    </Modal>
  );
}
